// src/AnnouncementsFeed.tsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ... (Keep Interfaces and Helpers unchanged) ...
interface Announcement {
  id: string;
  scope: 'Global' | 'Guild' | 'Personal';
  title: string;
  subtitle: string | null;
  body: string | null;
  guild_name: string | null;
  is_pinned: boolean;
  created_at: string;
  expires_at: string | null;
}

const SCOPE_THEME: Record<string, { text: string; bg: string; dot: string; glow: string }> = {
  Global:   { text: 'text-cyan-700 dark:text-cyan-400',   bg: 'bg-cyan-500/10',   dot: 'bg-cyan-400',   glow: 'rgba(6, 182, 212, 0.15)' },
  Guild:    { text: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-500/10', dot: 'bg-purple-400', glow: 'rgba(168, 85, 247, 0.15)' },
  Personal: { text: 'text-amber-700 dark:text-amber-400',  bg: 'bg-amber-500/10',  dot: 'bg-amber-400',  glow: 'rgba(245, 158, 11, 0.15)' },
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ... (Keep AnnouncementModal exactly as it was) ...
function AnnouncementModal({ announcement, onClose }: { announcement: Announcement; onClose: () => void; }) {
  const style = SCOPE_THEME[announcement.scope] || SCOPE_THEME.Global;
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: 'linear-gradient(to bottom, #0c1018 0%, #080b10 100%)' }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px] pointer-events-none" style={{ background: style.glow }} />
      <div className="relative shrink-0 border-b border-[var(--c-line)]">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <button onClick={onClose} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)] transition-all">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              <span className="text-sm">Back</span>
            </button>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full ${style.text} ${style.bg} border border-[var(--c-line-soft)]`}>{announcement.scope}</span>
              <span className="text-sm text-[var(--c-ink-3)]">{formatDate(announcement.created_at)}</span>
            </div>
          </div>
          <div className="space-y-3">
            {announcement.guild_name && <div className="flex items-center gap-2 text-sm text-[var(--c-ink-3)]"><span className="uppercase tracking-wider text-[10px]">From</span>{announcement.guild_name}</div>}
            <h1 className="text-3xl md:text-4xl font-bold text-[var(--c-ink)] tracking-tight">{announcement.title}</h1>
            {announcement.subtitle && <p className="text-xl text-[var(--c-ink-3)] font-light">{announcement.subtitle}</p>}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(to right, transparent, ${style.glow.replace('0.15', '0.5')}, transparent)` }} />
      </div>
      <div className="relative flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-10">
          {announcement.body ? (
            <div className="announcement-body text-[var(--c-ink)] text-lg leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '1.125rem', lineHeight: '1.8' }} dangerouslySetInnerHTML={{ __html: announcement.body }} />
          ) : (
            <p className="text-[var(--c-ink-3)] italic text-center py-12">No additional content.</p>
          )}
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(modalContent, document.body);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AnnouncementsFeed() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  useEffect(() => {
    fetch('/api/announcements', { credentials: 'include' })
      .then(res => res.ok ? res.json() : [])
      .then(setAnnouncements)
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <span className="text-[var(--c-ink-4)] text-[10px] animate-pulse py-1">Loading...</span>;
  if (announcements.length === 0) return null;

  return (
    <>
      {announcements.map((ann, index) => {
        const theme = SCOPE_THEME[ann.scope] || SCOPE_THEME.Global;
        const hasBody = !!ann.body;
        const showSeparator = index > 0;

        return (
          <React.Fragment key={ann.id}>
            {showSeparator && (
              <span className="text-[var(--c-ink-4)] select-none mx-2 text-[10px] font-light">/</span>
            )}
            
            {/* INTERACTIVE PILL
               - Uses inline-flex so it flows left-to-right but acts like a box
               - Hover background makes it clear it's interactive
               - items-center fixes the alignment issues
            */}
            <button
              onClick={() => hasBody && setSelectedAnnouncement(ann)}
              disabled={!hasBody}
              className={`
                inline-flex items-center gap-2 px-2.5 py-1 rounded-md transition-all duration-200 group
                ${hasBody 
                  ? 'hover:bg-[var(--c-sunken-2)] cursor-pointer active:scale-95' 
                  : 'cursor-default opacity-70'}
              `}
            >
              {/* Dot with glow */}
              <span className="relative flex h-1.5 w-1.5">
                {index === 0 && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${theme.dot} opacity-75`}></span>}
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${theme.dot}`}></span>
              </span>

              {/* Title */}
              <span className="text-sm font-medium text-[var(--c-ink)] group-hover:text-[var(--c-ink)] tracking-tight">
                {ann.title}
              </span>

              {/* Date (Subtle but readable) */}
              <span className="text-[10px] text-[var(--c-ink-3)] font-mono group-hover:text-[var(--c-ink-2)] transition-colors ml-1">
                {formatDate(ann.created_at)}
              </span>
            </button>
          </React.Fragment>
        );
      })}

      {selectedAnnouncement && (
        <AnnouncementModal
          announcement={selectedAnnouncement}
          onClose={() => setSelectedAnnouncement(null)}
        />
      )}
    </>
  );
}